/**
 * STORAGE SERVICE - Infraestructura Base
 * 
 * Gestión de archivos y uploads
 */

import { storage } from '@/core/config/firebase';
import { Logger } from '@boosting/core';
import { ValidationError } from '../utils/errors';
import type { LogMedia } from '@/core/types/app-state';

export interface UploadOptions {
  maxSize?: number; // bytes
  allowedTypes?: string[];
  onProgress?: (progress: number) => void;
}

export interface UploadResult {
  path: string;
  downloadURL: string;
  size: number;
  contentType: string;
  uploadedAt: string;
}

export class StorageService {
  private storageRef = storage.ref();
  private logger = Logger.getLogger('StorageService');

  /**
   * Upload file
   */
  async uploadFile(
    file: File,
    path: string,
    options: UploadOptions = {}
  ): Promise<UploadResult> {
    // Validate
    this.validateFile(file, options);

    // Generate unique filename
    const fileName = this.generateFileName(file.name);
    const fullPath = `${path}/${fileName}`;

    const metadata = {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedBy: this.getCurrentUserId() || 'anonymous',
        uploadedAt: new Date().toISOString()
      }
    };

    // Upload
    const uploadTask = this.storageRef.child(fullPath).put(file, metadata);

    // Track progress
    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        options.onProgress?.(progress);
      },
      (error) => {
        this.logger.error('Upload failed', error as Error, { path: fullPath });
        throw error;
      }
    );

    // Wait for completion
    await uploadTask;

    // Get download URL
    const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();

    this.logger.info('File uploaded', {
      path: fullPath,
      size: file.size,
      type: file.type
    });

    return {
      path: fullPath,
      downloadURL,
      size: file.size,
      contentType: file.type,
      uploadedAt: new Date().toISOString()
    };
  }

  /**
   * Delete file
   */
  async deleteFile(path: string): Promise<void> {
    try {
      await this.storageRef.child(path).delete();
      this.logger.info('File deleted', { path });
    } catch (error) {
      this.logger.error('Delete failed', error as Error, { path });
      throw error;
    }
  }

  /**
   * Get file URL
   */
  async getFileURL(path: string): Promise<string> {
    return await this.storageRef.child(path).getDownloadURL();
  }

  /**
   * List files in path
   */
  async listFiles(path: string): Promise<Array<{ name: string; fullPath: string }>> {
    const ref = this.storageRef.child(path);
    const result = await ref.listAll();

    return result.items.map(item => ({
      name: item.name,
      fullPath: item.fullPath
    }));
  }

  /**
   * Validate file
   */
  private validateFile(file: File, options: UploadOptions): void {
    // Max size
    const maxSize = options.maxSize || 10 * 1024 * 1024; // 10MB default
    if (file.size > maxSize) {
      throw new ValidationError(
        `Archivo muy grande. Máximo: ${Math.round(maxSize / 1024 / 1024)}MB`
      );
    }

    // Allowed types
    const allowedTypes = options.allowedTypes || [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
      'application/pdf',
      'audio/mpeg',
      'audio/wav',
      'video/mp4'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new ValidationError(
        `Tipo de archivo no permitido: ${file.type}`
      );
    }
  }

  /**
   * Generate unique filename
   */
  private generateFileName(originalName: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(7);
    const extension = originalName.split('.').pop();
    return `${timestamp}-${random}.${extension}`;
  }

  private getCurrentUserId(): string {
    // Primero intentar obtener del contexto global de Auth
    const userId = (window as any).__CURRENT_USER_ID__;
    if (userId) return userId;
    
    // Fallback: intentar obtener directamente de firebase
    const auth = (window as any).firebase?.auth();
    return auth?.currentUser?.uid || 'system';
  }
}

export const storageService = new StorageService();

/**
 * Upload user-specific media (photos, audio) and return metadata
 */
export const uploadUserMedia = async (file: File | Blob, userId: string): Promise<LogMedia> => {
  const isFile = file instanceof File;
  const fileName = isFile ? file.name : `recording-${new Date().toISOString()}.webm`;
  const filePath = `user-media/${userId}/${Date.now()}-${fileName}`;
  const fileRef = storage.ref().child(filePath);

  const snapshot = await fileRef.put(file);
  const url = await snapshot.ref.getDownloadURL();

  return {
    type: file.type.startsWith('image/') ? 'photo' : 'audio',
    url,
    storagePath: filePath,
    mimeType: file.type,
  };
};

/**
 * Upload media for the shared content library
 */
export const uploadLibraryMedia = async (
  file: File | Blob
): Promise<{ url: string; storagePath: string }> => {
  const isFile = file instanceof File;
  const fileName = isFile ? file.name : `library-media-${new Date().toISOString()}`;
  const filePath = `library-media/${Date.now()}-${fileName}`;
  const fileRef = storage.ref().child(filePath);

  const snapshot = await fileRef.put(file);
  const url = await snapshot.ref.getDownloadURL();

  return {
    url,
    storagePath: filePath,
  };
};

/**
 * Delete media helper compatible with legacy components
 */
export const deleteMedia = async (storagePath: string): Promise<void> => {
  if (!storagePath) return;

  try {
    const fileRef = storage.ref().child(storagePath);
    await fileRef.delete();
  } catch (error: any) {
    if (error.code !== 'storage/object-not-found') {
      Logger.getLogger('StorageService').error('Error deleting media file', error, { storagePath });
    }
  }
};

